import { useApplicantAPI, useRequest } from "@certn/api";
import {
  Application,
  ApplicationForm,
  ApplicationHeader,
  ApplicationSectionList,
  SectionSchema,
} from "@certn/application";
import { ApplicationSectionListItem } from "@certn/application/src/ApplicationSectionListItem";
import { ApplicantToPayProvider } from "./ApplicantToPayContext";
import { ApplicationSummary } from "./ApplicationSummary";
import { PaymentSummary } from "./PaymentSummary";
import { Address, Text } from "@certn/cascadia";
import {
  BrandProvider,
  Container,
  Layout,
  NavigationSidebar,
} from "@certn/cascadia-react";
import { getRegionKey, useTimeTracking } from "@certn/utils";
import { JsonFormsCore } from "@jsonforms/core";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import constants from "../../../constants";
import { AppFooter } from "../../components/app-footer";
import { AppLogo } from "../../components/app-header/AppLogo";
import { useDocumentTitle } from "../../hooks/api/useDocumentTitle";
import { useLanguage } from "../../hooks/useLanguage";
import {
  APPLICATION_SECTION_PAYMENT_SUMMARY,
  APPLICATION_SECTION_SUMMARY,
  applicationSectionTitle,
} from "./applicationSectionTitle";

export function ApplicationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { applicantPortalUpdatedSubmitPage, enableFpCoBrandingV2 } = useFlags();
  const { caseId, sectionName: routeSectionName } = useParams();

  const { data, mutate } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/` as "/api/internal/applicant/case/{id}/"
  );
  const request = useRequest();
  const { i18n } = useTranslation();

  const { changeLanguage } = useLanguage();

  const { stopTimeTracker } = useTimeTracking();

  const auRegionWrongLanguage =
    getRegionKey() === "au" && i18n.language !== "en-AU";

  if (auRegionWrongLanguage) {
    changeLanguage("en-AU");
  }

  const sectionIsPaymentSummary =
    routeSectionName === APPLICATION_SECTION_PAYMENT_SUMMARY;

  useDocumentTitle(
    data
      ? t("{{section}} — Certn", {
          section: applicationSectionTitle(
            routeSectionName,
            data.ui_schema,
            t,
            data.is_applicant_to_pay ?? false
          ),
        })
      : null
  );

  // Navigation protection: require summary to be completed before accessing payment-summary
  useEffect(() => {
    if (
      sectionIsPaymentSummary &&
      data?.section_statuses?.summary !== "COMPLETE"
    ) {
      navigate(`/apply/${caseId}/${APPLICATION_SECTION_SUMMARY}`, {
        replace: true,
      });
    }
  }, [
    sectionIsPaymentSummary,
    data?.section_statuses?.summary,
    navigate,
    caseId,
  ]);

  const handleSave = async (values: JsonFormsCore["data"], validate = true) => {
    // Summary and payment-summary are special sections that don't have form data to save
    if (
      routeSectionName === APPLICATION_SECTION_SUMMARY ||
      sectionIsPaymentSummary
    ) {
      return;
    }

    await request(
      `/api/internal/applicant/case/${caseId}/update-section/${routeSectionName}/?validate=${validate}`,
      {
        method: "POST",
        body: JSON.stringify(values),
      }
    );
    const response = await mutate();
    return response?.applicant_form_data;
  };

  const handleSubmit = async () => {
    // If on summary route with applicant-to-pay, mark summary as complete and navigate to payment-summary page
    if (
      data?.is_applicant_to_pay &&
      routeSectionName === APPLICATION_SECTION_SUMMARY
    ) {
      try {
        // Shallow submit: mark the summary section as complete without full case submission
        await request(
          `/api/internal/applicant/case/${caseId}/update-section/${APPLICATION_SECTION_SUMMARY}/`,
          {
            method: "POST",
            body: JSON.stringify({}),
          }
        );
        // Refresh case data to update section_statuses before navigating
        // This ensures navigation protection doesn't redirect back to summary
        await mutate();
        // Navigate to payment-summary page after data is refreshed
        navigate(`/apply/${caseId}/${APPLICATION_SECTION_PAYMENT_SUMMARY}`, {
          replace: true,
        });
      } catch (error) {
        throw error;
      }
      return;
    }

    await request(`/api/internal/applicant/case/${caseId}/submit/`, {
      method: "POST",
      body: JSON.stringify(data?.applicant_form_data),
    });
    stopTimeTracker("application--submit");
    if (applicantPortalUpdatedSubmitPage) {
      navigate(`/apply/${caseId}/submitted`);
    } else {
      navigate("/", { state: { showSuccessModal: true } });
    }
  };

  if (!data) {
    // TODO: return something else
    return null;
  }

  // Determine additional sections based on applicant-to-pay
  const isApplicantToPay = data.is_applicant_to_pay ?? false;
  const isSummaryCompleted = data?.section_statuses?.summary === "COMPLETE";
  const additionalSections = isApplicantToPay ? (
    <ApplicationSectionListItem
      id={APPLICATION_SECTION_PAYMENT_SUMMARY}
      sectionKey={APPLICATION_SECTION_PAYMENT_SUMMARY}
      icon="pending_actions"
      isDisabled={!isSummaryCompleted}
    >
      <Trans>Payment Details</Trans>
    </ApplicationSectionListItem>
  ) : null;

  const applicationContent = (
    <ApplicantToPayProvider
      isApplicantToPay={data.is_applicant_to_pay ?? false}
    >
      <Application
        id={caseId ?? ""}
        locale={i18n.language}
        schema={data.data_schema}
        uischema={data.ui_schema as any}
        currentSection={routeSectionName}
        data={data.applicant_form_data}
        onRefresh={() => {
          void mutate();
        }}
        onNavigateToSection={(
          section: string | SectionSchema,
          type?: "replace" | "navigate"
        ) => {
          const sectionName =
            typeof section === "string" ? section : section.name;
          // Navigation protection: prevent navigation to payment-summary if summary is not completed
          if (
            sectionName === APPLICATION_SECTION_PAYMENT_SUMMARY &&
            data?.section_statuses?.summary !== "COMPLETE"
          ) {
            navigate(`/apply/${caseId}/${APPLICATION_SECTION_SUMMARY}`, {
              replace: type === "replace",
            });
            return;
          }
          // Navigate directly - payment-summary is both the section identifier and route
          navigate(`/apply/${caseId}/${sectionName}`, {
            replace: type === "replace",
          });
        }}
        onSubmitApplication={handleSubmit}
        onSave={handleSave}
        customerName={data.customer_name}
        clientGroupName={data.client_group_name}
        clientGroupAddress={data.client_group_address as Address}
        environment={constants.MODE}
        generatePresignedUrlEndpoint="/api/internal/applicant/generate-presigned-url/"
        downloadUrl={`/api/internal/applicant/case/${caseId}/file/download/`}
        sectionStatuses={data.section_statuses}
        branding={data.co_branding || {}}
        summaryComponent={ApplicationSummary}
        additionalSections={additionalSections}
      >
        <Layout
          menuPosition="left"
          layout="applicant/narrow"
          header={<ApplicationHeader logo={<AppLogo />} />}
          footer={<AppFooter />}
          navigation={
            <NavigationSidebar
              toolbar={
                <div className={css({ maxWidth: 92, maxHeight: 27 })}>
                  <AppLogo type="full" />
                </div>
              }
              width="wide"
            >
              {data.customer_name && (
                <div className={styles.titleContainer}>
                  <Text style="summary/section-title">
                    <Trans>APPLICATION FOR</Trans>
                  </Text>
                  <Text
                    style="summary/content-primary"
                    testID="application-navigation-panel-team-name"
                  >
                    {data.customer_name}
                  </Text>
                </div>
              )}
              <ApplicationSectionList
                isSummaryCompleted={
                  data.section_statuses.summary === "COMPLETE"
                }
                summaryText={
                  isApplicantToPay ? t("Review & Confirm") : undefined
                }
              />
            </NavigationSidebar>
          }
        >
          <Container
            className={
              sectionIsPaymentSummary
                ? styles.paymentSummaryContainer
                : undefined
            }
          >
            {sectionIsPaymentSummary ? <PaymentSummary /> : <ApplicationForm />}
          </Container>
        </Layout>
      </Application>
    </ApplicantToPayProvider>
  );

  return enableFpCoBrandingV2 ? (
    <BrandProvider
      primaryColor={data.co_branding?.org_primary_color ?? undefined}
    >
      {applicationContent}
    </BrandProvider>
  ) : (
    applicationContent
  );
}

const styles = {
  titleContainer: css({
    display: "flex",
    flexDirection: "column",
    height: "40px",
    paddingTop: "var(--space-6)",
    paddingBottom: "var(--space-6)",
    paddingLeft: "var(--space-5)",
    paddingRight: "var(--space-4)",
  }),
  paymentSummaryContainer: css({
    "@media screen and (min-width: 1024px)": {
      maxWidth: "90vw !important",
    },
  }),
};
